import React, { FC } from "react";
import { defineMessages, useIntl } from "react-intl";
import { PartnerInfoData } from "@app/provider/order";
import { Button, DateInput, Form, FormField, Textfield } from "@natera/form";
import { HeapPHIDecorator } from "@app/components/analytics";
import {
  useDialog,
  useErrorController,
  usePhoneValidation,
} from "@natera/platform/lib/hooks";
import { isPatientMinor, validateEmail, weekDayFormatter } from "@app/utils";
import { PhoneField } from "@app/components";
import PartnerInfoProvidingDialog from "@app/components/partnerInfo/partnerInfoProvidingDialog";
import Tooltip from "@natera/material/lib/tooltip";
import { getPhoneValidationErrorMessage } from "@app/utils/getPhoneValidationErrorMessage";
import { ConfigContext, UserContext } from "@app/provider";
import DateIconTriggerButton from "@app/components/partnerInfo/dateIconTriggerButton";

const messages = defineMessages({
  partnerInfoNext: {
    id: "partnerInfoNext",
    defaultMessage: "Next",
  },
  partnerInfoBack: {
    id: "partnerInfoBack",
    defaultMessage: "Back",
  },
  partnerInfoFormFirstName: {
    id: "partnerInfoFormFirstName",
    defaultMessage: "First Name",
  },
  setupPartnerInfoFormFirstNamePlaceholder: {
    id: "setupPartnerInfoFormFirstNamePlaceholder",
    defaultMessage: "Enter Your Partner’s First Name",
  },
  partnerInfoFormMiddleName: {
    id: "partnerInfoFormMiddleName",
    defaultMessage: "Middle Name",
  },
  setupPartnerInfoFormMiddleNamePlaceholder: {
    id: "setupPartnerInfoFormMiddleNamePlaceholder",
    defaultMessage: "Enter Your Partner’s Middle Name",
  },
  partnerInfoFormLastName: {
    id: "partnerInfoFormLastName",
    defaultMessage: "Last Name",
  },
  setupPartnerInfoFormLastNamePlaceholder: {
    id: "setupPartnerInfoFormLastNamePlaceholder",
    defaultMessage: "Enter Your Partner’s Last Name",
  },
  partnerInfoFormDOB: {
    id: "partnerInfoFormDOB",
    defaultMessage: "Date of Birth",
  },
  setupPartnerInfoFormDOBPlaceholder: {
    id: "setupPartnerInfoFormDOBPlaceholder",
    defaultMessage: "MM/DD/YYYY",
  },
  partnerInfoFormEmail: {
    id: "partnerInfoFormEmail",
    defaultMessage: "Email",
  },
  setupPartnerInfoFormEmaiPlaceholder: {
    id: "setupPartnerInfoFormEmaiPlaceholder",
    defaultMessage: "Enter Your Partner’s Email",
  },
  partnerInfoFormUnder18DateOfBirth: {
    id: "partnerInfoFormUnder18DateOfBirth",
    defaultMessage: "Your partner must be 18 years old or older",
  },
  partnerInfoFormInvalidEmail: {
    id: "partnerInfoFormInvalidEmail",
    defaultMessage: "Please enter a valid email address. (username@domain)",
  },
  partnerInfoFormEmailFormat: {
    id: "partnerInfoFormEmailFormat",
    defaultMessage: "Email format (username@domain)",
  },
});

export interface PartnerInfoFormProps {
  initialPartnerInfo?: PartnerInfoData;
  onSubmit: (partner: PartnerInfoData) => Promise<void>;
  onBack: () => void;
}

const PartnerInfoForm: FC<PartnerInfoFormProps> = ({
  initialPartnerInfo,
  onSubmit,
  onBack,
}) => {
  const intl = useIntl();
  const { config } = React.useContext(ConfigContext);
  const usePlusSignInEmailAddress =
    config.test.usePlusSignInEmailAddress.enabled;

  const errorController = useErrorController();
  const phoneValidation = usePhoneValidation();
  const { validatePhoneNumber: validateFromPS } = React.useContext(UserContext);
  const partnerInfoProvidingDialog = useDialog(PartnerInfoProvidingDialog);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    errorController.clearErrors();

    const formData = new FormData(e.currentTarget);

    const firstName = (formData.get("firstName") as string | null)?.trim();
    const middleName = (formData.get("middleName") as string | null)?.trim();
    const lastName = (formData.get("lastName") as string | null)?.trim();
    const dateOfBirth = formData.get("dateOfBirth")?.toString().trim();
    const mobileNumber = formData.get("mobileNumber")?.toString().trim();
    const email = formData.get("email")?.toString().trim();

    // Complete partner details require identity fields plus at least one
    // contact method (phone or email) so results can be shared. See UPP-5712.
    const hasRequiredPartnerDetails =
      Boolean(firstName) &&
      Boolean(lastName) &&
      Boolean(dateOfBirth) &&
      Boolean(mobileNumber || email);

    const submitData = () =>
      onSubmit({
        firstName,
        middleName,
        lastName,
        dateOfBirth,
        email,
        phone: mobileNumber,
      });

    if (dateOfBirth && isPatientMinor(new Date(dateOfBirth))) {
      errorController.setValidationError(
        "dateOfBirth",
        intl.formatMessage(messages.partnerInfoFormUnder18DateOfBirth)
      );

      return;
    }

    if (mobileNumber) {
      const phoneValidationErrorMessage = await getPhoneValidationErrorMessage({
        isMandatory: false,
        mobileNumber,
        intl,
        phoneValidation,
        validateFromPS,
      });

      if (phoneValidationErrorMessage) {
        errorController.setValidationError(
          "mobileNumber",
          phoneValidationErrorMessage
        );
        return;
      }
    }

    if (email && !validateEmail(email, usePlusSignInEmailAddress)) {
      errorController.setValidationError(
        "email",
        intl.formatMessage(messages.partnerInfoFormInvalidEmail)
      );
      return;
    }

    if (!hasRequiredPartnerDetails) {
      partnerInfoProvidingDialog.open({
        onClose: () => partnerInfoProvidingDialog.close(),
        onContinue: submitData,
      });
      return;
    }

    submitData();
  };

  return (
    <>
      {partnerInfoProvidingDialog.getDialog()}
      <Form
        className="partner-info-form__container"
        noValidate
        onSubmit={handleSubmit}
      >
        <FormField
          label={intl.formatMessage(messages.partnerInfoFormFirstName)}
          error={errorController.getValidationError("firstName")}
          htmlFor="firstName"
        >
          <HeapPHIDecorator protectAttr={["value"]}>
            <Textfield
              placeholder={intl.formatMessage(
                messages.setupPartnerInfoFormFirstNamePlaceholder
              )}
              id="firstName"
              name="firstName"
              defaultValue={initialPartnerInfo?.firstName}
              outline
              maxLength={255}
              autoComplete="given-name"
            />
          </HeapPHIDecorator>
        </FormField>
        <FormField
          label={intl.formatMessage(messages.partnerInfoFormMiddleName)}
          error={errorController.getValidationError("middleName")}
          htmlFor="middleName"
        >
          <HeapPHIDecorator protectAttr={["value"]}>
            <Textfield
              placeholder={intl.formatMessage(
                messages.setupPartnerInfoFormMiddleNamePlaceholder
              )}
              id="middleName"
              name="middleName"
              defaultValue={initialPartnerInfo?.middleName}
              outline
              maxLength={255}
              autoComplete="additional-name"
            />
          </HeapPHIDecorator>
        </FormField>
        <FormField
          label={intl.formatMessage(messages.partnerInfoFormLastName)}
          error={errorController.getValidationError("lastName")}
          htmlFor="lastName"
        >
          <HeapPHIDecorator protectAttr={["value"]}>
            <Textfield
              placeholder={intl.formatMessage(
                messages.setupPartnerInfoFormLastNamePlaceholder
              )}
              id="lastName"
              name="lastName"
              defaultValue={initialPartnerInfo?.lastName}
              outline
              maxLength={255}
              autoComplete="family-name"
            />
          </HeapPHIDecorator>
        </FormField>
        <FormField
          label={intl.formatMessage(messages.partnerInfoFormDOB)}
          error={errorController.getValidationError("dateOfBirth")}
          htmlFor="date"
        >
          <HeapPHIDecorator protectAttr={["value"]}>
            <DateInput
              id="date"
              name="dateOfBirth"
              maxDate={new Date()}
              defaultDate={
                initialPartnerInfo?.dateOfBirth
                  ? new Date(initialPartnerInfo.dateOfBirth)
                  : undefined
              }
              onDateChange={() =>
                errorController.clearValidationError("dateOfBirth")
              }
              placeholder={intl.formatMessage(
                messages.setupPartnerInfoFormDOBPlaceholder
              )}
              outline
              trailingIcon={<DateIconTriggerButton />}
              autoComplete="bday"
              formatWeekdayName={weekDayFormatter}
            />
          </HeapPHIDecorator>
        </FormField>
        <PhoneField
          currentPhone={initialPartnerInfo?.phone}
          getValidationError={errorController.getValidationError}
          required={false}
        />
        <>
          <div id="instructions" className="visually-hidden">
            {intl.formatMessage(messages.partnerInfoFormEmailFormat)}
          </div>
          <FormField
            label={intl.formatMessage(messages.partnerInfoFormEmail)}
            error={errorController.getValidationError("email")}
            htmlFor="email"
            className="partner-info-email"
          >
            <Tooltip
              content={intl.formatMessage(messages.partnerInfoFormEmailFormat)}
            >
              <Textfield
                id="email"
                name="email"
                type="email"
                placeholder={intl.formatMessage(
                  messages.setupPartnerInfoFormEmaiPlaceholder
                )}
                defaultValue={initialPartnerInfo?.email}
                autoComplete="email"
                outline
                aria-describedby="instructions"
              />
            </Tooltip>
          </FormField>
        </>

        <div className="stepper__actions">
          <Button type="submit" data-testid="submit" unelevated>
            {intl.formatMessage(messages.partnerInfoNext)}
          </Button>
          <Button onClick={onBack} outlined>
            {intl.formatMessage(messages.partnerInfoBack)}
          </Button>
        </div>
      </Form>
    </>
  );
};

export default PartnerInfoForm;
